import { Stack, Typography } from '@mui/material';
import {
  ConductConnotation,
  ConductSelectTypesFilterValue,
  useGetConductTypesForSchoolQuery,
} from '@schooly/api';
import { FC, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import {
  filterConductTypes,
  renderConductTypeOptionTags,
  renderConductTypeTags,
} from './ConductTypeExpandedSelect';
import { ConductTypeSelectRow } from './ConductTypeSelectRow';
import { onSelectConductTypeOrConductTypeOption } from './selectorHelpers';

type ConductTypeOptionsSelectMultipleProps = {
  schoolId: string;
  selectedConductTypeIds: string[];
  selectedConductTypeOptions: ConductSelectTypesFilterValue[];
  onSelectConductTypeId: (id: string) => void;
  onSelectConductTypeOption: (conductTypeId: string, label: string) => void;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'>;

export const ConductTypeOptionsSelectMultiple: FC<ConductTypeOptionsSelectMultipleProps> = ({
  schoolId,
  selectedConductTypeIds,
  selectedConductTypeOptions,
  onSelectConductTypeId,
  onSelectConductTypeOption,
  placeholder,
  ...dropdownProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { data } = useGetConductTypesForSchoolQuery(schoolId, { refetchOnMount: 'always' });
  const [query, setQuery] = useState('');

  const handleSelectConductTypeOption = useCallback(
    (conductTypeId: string, label: string) => {
      onSelectConductTypeOrConductTypeOption({
        conductTypes: data,
        selectedConductTypeOptions,
        onSelectConductTypeId,
        onSelectConductTypeOption,
      })(conductTypeId, label);
    },
    [data, onSelectConductTypeId, onSelectConductTypeOption, selectedConductTypeOptions],
  );

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const positive = data
      .filter((ct) => ct.connotation === ConductConnotation.POSITIVE)
      .filter((ct) => filterConductTypes(ct, query));
    const negative = data
      .filter((ct) => ct.connotation === ConductConnotation.NEGATIVE)
      .filter((ct) => filterConductTypes(ct, query));

    if (!positive.length && !negative.length)
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );

    return (
      <Stack gap={1} m={1}>
        {!!positive.length && (
          <Stack gap={1}>
            <Typography variant="h4">
              <FormattedMessage id="conduct-Type-positive" />
            </Typography>

            {positive.map((conductType) => (
              <ConductTypeSelectRow
                key={conductType.id}
                isSelected={selectedConductTypeIds.includes(conductType.id)}
                onSelectConductType={onSelectConductTypeId}
                onSelectConductTypeOption={handleSelectConductTypeOption}
                conductType={conductType}
                selectedConductTypeOptions={
                  selectedConductTypeOptions.find((ct) => ct.conduct_type_id === conductType.id)
                    ?.conduct_type_options ?? []
                }
              />
            ))}
          </Stack>
        )}
        {!!negative.length && (
          <Stack gap={1}>
            <Typography variant="h4">
              <FormattedMessage id="conduct-Type-negative" />
            </Typography>

            {negative.map((conductType) => (
              <ConductTypeSelectRow
                key={conductType.id}
                isSelected={selectedConductTypeIds.includes(conductType.id)}
                onSelectConductType={onSelectConductTypeId}
                onSelectConductTypeOption={handleSelectConductTypeOption}
                conductType={conductType}
                selectedConductTypeOptions={
                  selectedConductTypeOptions.find((ct) => ct.conduct_type_id === conductType.id)
                    ?.conduct_type_options ?? []
                }
              />
            ))}
          </Stack>
        )}
      </Stack>
    );
  }, [
    data,
    handleSelectConductTypeOption,
    onSelectConductTypeId,
    query,
    selectedConductTypeIds,
    selectedConductTypeOptions,
  ]);

  return (
    <DropdownSelect
      {...dropdownProps}
      placeholder={placeholder}
      hasValues={selectedConductTypeIds.length > 0 || selectedConductTypeOptions.length > 0}
      renderContent={renderContent}
    >
      {(opened) => (
        <>
          {renderConductTypeTags({
            ids: selectedConductTypeIds,
            tagProps: { schoolId, size: 'small', sx: { maxWidth: 200 } },
            onDelete: opened ? onSelectConductTypeId : undefined,
          })}
          {renderConductTypeOptionTags({
            options: selectedConductTypeOptions,
            onDelete: opened ? onSelectConductTypeOption : undefined,
            tagProps: { schoolId, size: 'small', sx: { maxWidth: 200 } },
          })}

          {opened && (
            <SelectSearchInput ref={inputRef} autoFocus value={query} onChangeText={setQuery} />
          )}
        </>
      )}
    </DropdownSelect>
  );
};
